import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, X, Plus, Send, Loader2, Workflow, ListPlus, FileText, Share2, Check, ArrowUpRight, Trash2, History } from "lucide-react";
import { api, API, agencyToken, formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";

const SUGGESTIONS = [
  "Based on our current SEO projects, draft a task flow I can add to our SEO projects",
  "Who on the team is overloaded this week?",
  "Which projects have overdue tasks?",
];

const inline = (s) => s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <b key={i}>{p.slice(2, -2)}</b>
  : p.startsWith("`") && p.endsWith("`") && p.length > 2 ? <code key={i} className="rounded bg-[#F1F1F4] px-1 text-[12px]">{p.slice(1, -1)}</code> : p));
function Md({ text }) {
  const blocks = []; let list = null;
  (text || "").split("\n").forEach((ln, i) => {
    const b = ln.match(/^\s*[-*]\s+(.*)/) || ln.match(/^\s*\d+[.)]\s+(.*)/);
    if (b) { if (!list) { list = []; blocks.push(<ul key={`l${i}`} className="list-disc pl-5 space-y-0.5">{list}</ul>); } list.push(<li key={i}>{inline(b[1])}</li>); return; }
    list = null;
    const h = ln.match(/^#{1,4}\s+(.*)/);
    if (h) blocks.push(<p key={i} className="font-semibold text-[#06060C] mt-2">{inline(h[1])}</p>);
    else if (ln.trim()) blocks.push(<p key={i}>{inline(ln)}</p>);
  });
  return <div className="space-y-1.5 text-[13px] leading-relaxed text-[#2a2b31]">{blocks}</div>;
}

const META = {
  create_task_flow: { icon: Workflow, title: (p) => `New task flow: ${p.name}` },
  import_task_flow: { icon: ListPlus, title: (p) => `Add “${p.flow}” to ${(p.projects || []).length} project${(p.projects || []).length === 1 ? "" : "s"}` },
  create_tasks: { icon: ListPlus, title: (p) => `${(p.tasks || []).length} task${(p.tasks || []).length === 1 ? "" : "s"} for ${p.project}` },
  start_doc_flow: { icon: FileText, title: (p) => `Start doc flow: ${p.doc_name}` },
  start_social_flow: { icon: Share2, title: (p) => `Start social flow: ${p.name}` },
};

function ActionCard({ mid, a, onDone }) {
  const nav = useNavigate();
  const [p, setP] = useState(a.payload);
  const [busy, setBusy] = useState("");
  const M = META[a.type];
  const go = async (op) => {
    setBusy(op);
    try { const { data } = await api.post(`/assistant/messages/${mid}/actions/${a.id}/${op}`, op === "apply" ? { payload: p } : {}); onDone(data); if (op === "apply") toast.success(data.result?.label || "Applied", { position: "bottom-left" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail), { position: "bottom-left" }); } finally { setBusy(""); }
  };
  const drop = (i) => setP({ ...p, tasks: p.tasks.filter((_, k) => k !== i).map((t) => (typeof t.depends_on === "number" ? { ...t, depends_on: t.depends_on === i ? null : t.depends_on > i ? t.depends_on - 1 : t.depends_on } : t)) });
  const pending = a.status === "pending";
  return (
    <div data-testid={`assistant-action-${a.id}`} className={`rounded-2xl border bg-white overflow-hidden ${pending ? "border-[#d9ccff]" : "border-[#EEEFEF] opacity-90"}`}>
      <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F7F4FF]"><M.icon className="h-4 w-4 text-[#723FFF]" /><p className="text-[13px] font-medium text-[#06060C] flex-1 truncate">{M.title(p)}</p></div>
      <div className="px-3.5 py-2.5 space-y-1.5 max-h-72 overflow-y-auto text-[12px] text-[#595D62]">
        {p.description && <p>{p.description}</p>}
        {(p.tasks || []).map((t, i) => (
          <div key={i} className="flex items-start gap-2 rounded-lg bg-[#FAFAFD] px-2.5 py-1.5" data-testid={`assistant-action-task-${i}`}>
            <span className="font-mono-data text-[10px] text-[#9FA1A4] mt-0.5 w-4">{i + 1}</span>
            <div className="flex-1 min-w-0"><p className="text-[#06060C]">{t.title}</p>
              <p className="text-[11px] text-[#9FA1A4]">{[t.due_offset_days != null && `day ${t.due_offset_days}`, t.due_date, t.time_estimate_minutes && `${t.time_estimate_minutes}m`, t.assign && t.assign !== "scheduler" && t.assign.replace(/^(position|person):/, ""), t.assignee, t.depends_on != null && `after #${t.depends_on + 1}`].filter(Boolean).join(" · ")}{t.subtasks?.length ? ` · ${t.subtasks.length} subtasks` : ""}</p></div>
            {pending && <button onClick={() => drop(i)} title="Remove" className="text-[#c4c5cc] hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
        {(p.projects || []).length > 0 && <p>Projects: {p.projects.join(", ")}</p>}
        {["project", "flow", "owner", "drafter", "due_date"].filter((k) => p[k] && a.type.startsWith("start")).map((k) => <p key={k}><span className="capitalize">{k.replace("_", " ")}</span>: <span className="text-[#06060C]">{p[k]}</span></p>)}
        {p.brief && a.type.startsWith("start") && <p className="line-clamp-3">Brief: {p.brief}</p>}
      </div>
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-t border-[#f3f3f6]">
        {pending ? (
          <>
            <button data-testid={`assistant-apply-${a.id}`} onClick={() => go("apply")} disabled={!!busy} className="rounded-full bg-[#06060C] text-white text-xs px-3.5 py-1.5 flex items-center gap-1.5 disabled:opacity-50">{busy === "apply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Apply</button>
            <button data-testid={`assistant-dismiss-${a.id}`} onClick={() => go("dismiss")} disabled={!!busy} className="rounded-full text-xs px-3 py-1.5 text-[#595D62] hover:bg-[#F6F6FD]">Dismiss</button>
          </>
        ) : a.status === "applied" ? (
          <span className="text-xs text-[#1f9d55] flex items-center gap-1.5 flex-1" data-testid={`assistant-applied-${a.id}`}><Check className="h-3.5 w-3.5" /> {a.result?.label}
            {a.result?.link && <button onClick={() => nav(a.result.link)} className="ml-auto text-[#723FFF] flex items-center gap-0.5">Open <ArrowUpRight className="h-3 w-3" /></button>}</span>
        ) : <span className="text-xs text-[#9FA1A4]">Dismissed</span>}
      </div>
    </div>
  );
}

function Message({ m, onAction }) {
  if (m.role === "user") return <div className="flex justify-end"><p className="max-w-[85%] rounded-2xl rounded-tr-md bg-[#06060C] text-white text-[13px] px-3.5 py-2 whitespace-pre-wrap">{m.text}</p></div>;
  return (
    <div className="space-y-2.5" data-testid="assistant-reply">
      {m.text ? <Md text={m.text} /> : <Loader2 className="h-4 w-4 animate-spin text-[#c4c5cc]" />}
      {(m.actions || []).map((a) => <ActionCard key={a.id} mid={m.id} a={a} onDone={(na) => onAction(m.id, na)} />)}
    </div>
  );
}

export function AssistantPanel({ onClose }) {
  const [threads, setThreads] = useState([]);
  const [tid, setTid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const end = useRef(null);
  const loadThreads = () => api.get("/assistant/threads").then(({ data }) => setThreads(data.threads)).catch(() => {});
  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs]);
  const open = async (id) => { setTid(id); setShowHist(false); const { data } = await api.get(`/assistant/threads/${id}/messages`); setMsgs(data.messages); };
  const fresh = () => { setTid(null); setMsgs([]); setShowHist(false); };
  const remove = async (id) => { await api.delete(`/assistant/threads/${id}`).catch(() => {}); if (id === tid) fresh(); loadThreads(); };
  const send = async (q) => {
    const body = (q ?? text).trim();
    if (!body || busy) return;
    setBusy(true); setText("");
    let id = tid;
    try {
      if (!id) { const { data } = await api.post("/assistant/threads"); id = data.id; setTid(id); }
      setMsgs((m) => [...m, { id: "u-tmp", role: "user", text: body }, { id: "a-tmp", role: "assistant", text: "" }]);
      const res = await fetch(`${API}/assistant/threads/${id}/messages`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${agencyToken()}` }, body: JSON.stringify({ text: body }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "The assistant couldn't answer");
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "", acc = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.t === "delta") { acc += ev.v; const shown = acc.replace(/```action[\s\S]*?(```|$)/g, "").trim(); setMsgs((m) => m.map((x) => (x.id === "a-tmp" ? { ...x, text: shown } : x))); }
          else if (ev.t === "done") setMsgs((m) => m.map((x) => (x.id === "a-tmp" ? ev.message : x.id === "u-tmp" ? ev.user_message : x)));
        }
      }
      loadThreads();
    } catch (e) { toast.error(e.message); setMsgs((m) => m.filter((x) => !x.id.endsWith("-tmp"))); } finally { setBusy(false); }
  };
  const onAction = (mid, na) => setMsgs((m) => m.map((x) => (x.id === mid ? { ...x, actions: x.actions.map((a) => (a.id === na.id ? na : a)) } : x)));
  return (
    <aside data-testid="assistant-panel" className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[460px] bg-white border-l border-[#EEEFEF] shadow-[-24px_0_60px_-30px_rgba(6,6,12,0.35)] flex flex-col animate-in slide-in-from-right-4 fade-in duration-200">
      <header className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-[#EEEFEF]">
        <span className="h-8 w-8 rounded-xl bg-[#06060C] text-white flex items-center justify-center"><Sparkles className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-[#06060C]">Assistant</p><p className="text-[11px] text-[#9FA1A4] truncate">Drafts are only created when you click Apply</p></div>
        <button data-testid="assistant-history" onClick={() => setShowHist((v) => !v)} title="Chats" className="h-8 w-8 rounded-full flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><History className="h-4 w-4" /></button>
        <button data-testid="assistant-new-chat" onClick={fresh} title="New chat" className="h-8 w-8 rounded-full flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><Plus className="h-4 w-4" /></button>
        <button data-testid="assistant-close" onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><X className="h-4 w-4" /></button>
      </header>
      {showHist && (
        <div className="shrink-0 max-h-64 overflow-y-auto border-b border-[#EEEFEF] bg-[#FAFAFD] p-2 space-y-0.5" data-testid="assistant-thread-list">
          {threads.map((t) => (
            <div key={t.id} className={`group flex items-center gap-2 rounded-lg px-2.5 py-1.5 ${t.id === tid ? "bg-white" : "hover:bg-white"}`}>
              <button data-testid={`assistant-thread-${t.id}`} onClick={() => open(t.id)} className="flex-1 text-left text-[13px] truncate">{t.title}</button>
              <button data-testid={`assistant-thread-delete-${t.id}`} onClick={() => remove(t.id)} title="Delete chat" className="md:opacity-0 md:group-hover:opacity-100 text-[#c4c5cc] hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {!threads.length && <p className="text-xs text-[#9FA1A4] px-2 py-2">No chats yet.</p>}
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="assistant-messages">
        {!msgs.length && (
          <div className="pt-6 space-y-2">
            <p className="font-display text-xl font-semibold text-[#06060C]">How can I help?</p>
            <p className="text-[13px] text-[#595D62] mb-3">I know your projects, team, workload and flows. I can draft task flows, tasks, and doc or social flows for you to apply.</p>
            {SUGGESTIONS.map((s, i) => <button key={s} data-testid={`assistant-suggestion-${i}`} onClick={() => send(s)} className="block w-full text-left rounded-xl border border-[#EEEFEF] px-3 py-2 text-[13px] text-[#06060C] hover:border-[#d9ccff] hover:bg-[#F7F4FF] transition-colors">{s}</button>)}
          </div>
        )}
        {msgs.map((m) => <Message key={m.id} m={m} onAction={onAction} />)}
        <div ref={end} />
      </div>
      <div className="shrink-0 border-t border-[#EEEFEF] p-3">
        <div className="flex items-end gap-2 rounded-2xl border border-[#EEEFEF] bg-[#F6F6FD] p-1.5 focus-within:border-[#d9ccff]">
          <Textarea data-testid="assistant-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask or tell me what to draft…" className="border-0 shadow-none bg-transparent focus-visible:ring-0 resize-none text-[13px] min-h-0" />
          <button data-testid="assistant-send" onClick={() => send()} disabled={busy || !text.trim()} className="h-9 w-9 shrink-0 rounded-full bg-[#723FFF] text-white flex items-center justify-center disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
        </div>
      </div>
    </aside>
  );
}

export function AssistantButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="assistant-open" onClick={() => setOpen((v) => !v)} title="Assistant" className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${open ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><Sparkles className="h-[18px] w-[18px]" /></button>
      {open && createPortal(<AssistantPanel onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}
